import { Typography } from "@/components/ui/typography"
import { useEmailSenderIdentity } from "@/features/material-compliance/api/inquiryEmailApi"
import type { InquiryTemplateSettings } from "@/features/material-compliance/components/supplier-declarations/inquiry/settings/templateSettingsMock"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { useOrganization } from "@/services/api/organizationApi"
import type { ReactNode } from "react"
import { Link } from "react-router"
import { LanguageStatus } from "../../../components/LanguageStatus"
import { TemplateSettingsLoader } from "../../../components/TemplateSettingsLoader"
import {
  useSettingsTarget,
  type SettingsTargetId,
} from "../../../settingsTargets"
import {
  languagesNeedingWriting,
  needsWriting,
} from "../../../setup/placeholders"
import { pdfFooterPrints } from "../../../setup/setupChecks"

const FORMAT_LABEL_KEYS: Record<
  InquiryTemplateSettings["defaultAttachmentFormat"],
  TranslationKeys
> = {
  pdf: "MC_INQ_DRAFT_FORMAT_PDF",
  excel: "MC_INQ_DRAFT_FORMAT_EXCEL",
  pdf_and_excel: "MC_INQ_DRAFT_FORMAT_PDF_EXCEL",
}

/** One line of the summary: what goes out, and the way to its setting. */
function Row({
  labelKey,
  target,
  children,
}: {
  labelKey: TranslationKeys
  target: SettingsTargetId
  children: ReactNode
}) {
  const { translate } = useTranslation()
  const { path, visible } = useSettingsTarget(target)
  const label = translate(labelKey)
  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 py-2">
      <dt className="w-32 shrink-0 text-sm text-muted-foreground">{label}</dt>
      <dd className="min-w-0 flex-1 text-sm">{children}</dd>
      {visible && (
        <dd>
          <Link
            to={path}
            replace
            aria-label={`${translate("SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_CHANGE")}: ${label}`}
            className="text-sm font-medium text-primary hover:underline"
          >
            {translate("SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_CHANGE")}
          </Link>
        </dd>
      )}
    </div>
  )
}

function Muted({ children }: { children: ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>
}

function Summary({ saved }: { saved: InquiryTemplateSettings }) {
  const { translate } = useTranslation()
  const { data: identity } = useEmailSenderIdentity()
  const { data: org } = useOrganization()
  const format = translate(FORMAT_LABEL_KEYS[saved.defaultAttachmentFormat])
  // Only the PDF files carry the logo and contact person; Excel is unbranded.
  const hasPdf = saved.defaultAttachmentFormat !== "excel"
  const notSet = (
    <Muted>{translate("SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_NOT_SET")}</Muted>
  )

  return (
    <dl className="flex flex-col divide-y">
      <Row
        labelKey="SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_SENDER"
        target="email.sendingAddress"
      >
        {identity?.fullAddress ?? notSet}
      </Row>
      <Row
        labelKey="SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_SIGNATURE"
        target="email.signature"
      >
        <LanguageStatus
          needsWriting={languagesNeedingWriting(saved.emailSignatures)}
        />
      </Row>
      <Row
        labelKey="SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_ATTACHMENT"
        target="inquiry.format"
      >
        {translate(
          saved.attachmentFormatLocked
            ? "SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_FORMAT_LOCKED"
            : "SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_FORMAT_CHOICE",
          { format },
        )}
      </Row>
      {hasPdf && (
        <Row
          labelKey="SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_LOGO"
          target="org.logo"
        >
          {org?.logoUrl ? (
            <img
              src={org.logoUrl}
              alt={translate("MC_ORG_SETTINGS_LOGO_ALT")}
              className="h-6 max-w-32 object-contain"
            />
          ) : (
            notSet
          )}
        </Row>
      )}
      {hasPdf && (
        <Row
          labelKey="SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_CONTACT"
          target="inquiry.contactPerson"
        >
          {saved.contactPerson.trim() || notSet}
        </Row>
      )}
      {pdfFooterPrints(saved) && (
        <Row
          labelKey="SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_FOOTER"
          target="inquiry.footer"
        >
          {needsWriting(saved.questionnaireFooter) ? (
            <span className="flex items-center gap-1.5">
              <span aria-hidden className="size-1.5 rounded-full bg-warning" />
              {translate("SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_PLACEHOLDER")}
            </span>
          ) : (
            translate("SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_WRITTEN")
          )}
        </Row>
      )}
    </dl>
  )
}

/**
 * On top of the Supplier inquiries page: what a supplier gets with every new
 * inquiry, as the settings stand, gathered from the pages and tabs that set
 * it, each line with the way to its setting.
 */
export function SupplierReceivesSummary() {
  const { translate } = useTranslation()
  return (
    <section className="flex flex-col gap-2 rounded-lg border p-4">
      <div className="flex flex-col gap-1">
        <Typography as="large" role="heading" aria-level={2}>
          {translate("SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_TITLE")}
        </Typography>
        <Typography as="muted">
          {translate("SETTINGS/SUPPLIER_INQUIRIES/SUMMARY_DESCRIPTION")}
        </Typography>
      </div>
      <TemplateSettingsLoader>
        {(settings) => <Summary saved={settings} />}
      </TemplateSettingsLoader>
    </section>
  )
}
